import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useEnvironment } from './env';
import { KEY_CODES, equals } from '../../utils/misc';
import { useCheckMidClick } from '../../utils/hooks/useCheckMidClick';
import { useUniqueId } from './uniqueIds';
import { useCheckMidKeyPress } from '../../utils/hooks/useCheckMidKeyPress';
export var MENU_ACTION_TYPE;
(function (MENU_ACTION_TYPE) {
    MENU_ACTION_TYPE["SUBMENU"] = "SUBMENU";
    MENU_ACTION_TYPE["LINK"] = "LINK";
})(MENU_ACTION_TYPE || (MENU_ACTION_TYPE = {}));
export var MENU_ORIENTATION;
(function (MENU_ORIENTATION) {
    MENU_ORIENTATION["HORIZONTAL"] = "HORIZONTAL";
    MENU_ORIENTATION["VERTICAL"] = "VERTICAL";
})(MENU_ORIENTATION || (MENU_ORIENTATION = {}));
const MenuContext = React.createContext({
    menu: { items: [], orientation: MENU_ORIENTATION.HORIZONTAL },
    activePath: [],
    isOpen: false,
    open: () => { },
    close: () => { },
    getMenuItem: () => undefined,
    getMenuItemProps: () => ({}),
    getMenuProps: () => ({}),
});
const getAriaRoleDescription = (orientation) => {
    if (orientation === MENU_ORIENTATION.HORIZONTAL) {
        return 'Menu item, press right arrow to navigate to next item, press enter to select';
    }
    else {
        return 'Menu item, press down arrow to navigate to next item, press enter to select';
    }
};
export const useMenu = () => React.useContext(MenuContext);
const activeStyles = {
    outline: '2px solid blue',
};
const inactiveStyles = {
    outline: 'none',
};
export const MenuProvider = ({ menu, children, }) => {
    const { flagInDevelopment } = useEnvironment();
    const menuId = useUniqueId('hcl-menu');
    const { isMidClick, elementProps: checkMidclickProps } = useCheckMidClick();
    const { isMidKeyPress, elementProps: checkMidKeyPressProps } = useCheckMidKeyPress();
    const closeOnBlurTimeout = React.useRef();
    const initialPath = React.useMemo(() => [menu.items[0].id], [menu]);
    const [activePath, setActivePath] = useState(initialPath);
    const [isOpen, setIsOpen] = useState(false);
    const getItemId = React.useCallback((path) => `${menuId}-item-${path.join('-')}`, [menuId]);
    useEffect(() => {
        if (activePath && isOpen) {
            const activeChild = window.document.getElementById(getItemId(activePath));
            activeChild === null || activeChild === void 0 ? void 0 : activeChild.focus();
            clearTimeout(closeOnBlurTimeout.current);
        }
    }, [activePath, getItemId, isOpen]);
    const open = React.useCallback(() => {
        setIsOpen(true);
    }, []);
    const close = React.useCallback(() => {
        setIsOpen(false);
    }, []);
    const getMenuItem = React.useCallback((path) => {
        let menuItem = undefined;
        for (const pathSegment of path) {
            if (!menuItem) {
                menuItem = menu.items.find((item) => item.id === pathSegment);
            }
            else if (menuItem.action.type === MENU_ACTION_TYPE.SUBMENU) {
                menuItem = menuItem.action.submenu.items.find((item) => item.id === pathSegment);
            }
            if (!menuItem) {
                return undefined;
            }
        }
        return menuItem;
    }, [menu]);
    const activeMenuItem = React.useMemo(() => getMenuItem(activePath), [activePath, getMenuItem]);
    const activeParentMenu = React.useMemo(() => {
        let parentMenu = menu;
        const tempActivePath = [...activePath];
        tempActivePath.pop();
        if (tempActivePath.length > 0) {
            const parentMenuItem = getMenuItem(tempActivePath);
            if ((parentMenuItem === null || parentMenuItem === void 0 ? void 0 : parentMenuItem.action.type) === MENU_ACTION_TYPE.SUBMENU) {
                parentMenu = parentMenuItem === null || parentMenuItem === void 0 ? void 0 : parentMenuItem.action.submenu;
            }
            else {
                flagInDevelopment('');
            }
        }
        return parentMenu;
    }, [activePath, menu, getMenuItem, flagInDevelopment]);
    const activeIndex = React.useMemo(() => {
        return activeParentMenu.items.findIndex((item) => item.id === activePath[activePath.length - 1]);
    }, [activeParentMenu, activePath]);
    const currentMenuLength = activeParentMenu.items.length;
    const onFocus = React.useCallback(() => {
        if (!isMidClick.current) {
            open();
        }
    }, [isMidClick, open]);
    const onBlur = React.useCallback(() => {
        checkMidKeyPressProps.onBlur();
        closeOnBlurTimeout.current = setTimeout(() => {
            close();
            setActivePath(initialPath);
        }, 300);
    }, [close, initialPath, checkMidKeyPressProps]);
    const onKeyDown = React.useCallback((e) => {
        checkMidKeyPressProps.onKeyDown(e);
        const navigateNext = () => {
            const nextIndex = (activeIndex + 1) % currentMenuLength;
            const nextId = activeParentMenu.items[nextIndex].id;
            setActivePath([...activePath.slice(0, activePath.length - 1), nextId]);
        };
        const navigatePrevious = () => {
            if (activeIndex === 0) {
                navigateOut();
            }
            else {
                const prevIndex = activeIndex - 1;
                const prevId = activeParentMenu.items[prevIndex].id;
                setActivePath([...activePath.slice(0, activePath.length - 1), prevId]);
            }
        };
        const navigateIn = () => {
            var _a;
            if (((_a = activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.action) === null || _a === void 0 ? void 0 : _a.type) === MENU_ACTION_TYPE.SUBMENU) {
                setActivePath([
                    ...activePath,
                    activeMenuItem.action.submenu.items[0].id,
                ]);
            }
            else {
                navigateNext();
            }
        };
        const navigateOut = () => {
            if (activePath.length > 1) {
                setActivePath(activePath.slice(0, activePath.length - 1));
            }
        };
        const select = () => {
            if ((activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.action.type) === MENU_ACTION_TYPE.SUBMENU) {
                setActivePath(activePath.concat([activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.id]));
            }
            else if ((activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.action.type) === MENU_ACTION_TYPE.LINK) {
                window.location.href = activeMenuItem.action.to;
            }
        };
        const isVertical = activeParentMenu.orientation === MENU_ORIENTATION.VERTICAL;
        if (e.key === KEY_CODES.ENTER) {
            if ((activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.action.type) === MENU_ACTION_TYPE.LINK) {
                select();
            }
            else if ((activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.action.type) === MENU_ACTION_TYPE.SUBMENU) {
                navigateIn();
            }
        }
        if (e.key === KEY_CODES.ESCAPE) {
            if (activePath.length > 1) {
                navigateOut();
            }
            else {
                const activeChild = window.document.getElementById(getItemId(activePath));
                activeChild === null || activeChild === void 0 ? void 0 : activeChild.blur();
            }
        }
        if (e.key === KEY_CODES.ARROW_DOWN) {
            if (isVertical) {
                navigateNext();
            }
            else {
                navigateIn();
            }
        }
        if (e.key === KEY_CODES.ARROW_UP) {
            if (isVertical) {
                navigatePrevious();
            }
            else {
                navigateOut();
            }
        }
        if (e.key === KEY_CODES.ARROW_RIGHT) {
            if (isVertical) {
                navigateIn();
            }
            else {
                navigateNext();
            }
        }
        if (e.key === KEY_CODES.ARROW_LEFT) {
            if (isVertical) {
                navigateOut();
            }
            else {
                navigatePrevious();
            }
        }
    }, [
        activePath,
        activeIndex,
        activeMenuItem,
        activeParentMenu,
        currentMenuLength,
        getItemId,
        checkMidKeyPressProps,
    ]);
    const getMenuProps = React.useCallback((path) => {
        const isTopLevel = !path;
        let _menu = menu;
        let menuItem = undefined;
        if (path) {
            menuItem = getMenuItem(path);
            if ((menuItem === null || menuItem === void 0 ? void 0 : menuItem.action.type) === MENU_ACTION_TYPE.SUBMENU) {
                _menu = menuItem.action.submenu;
            }
        }
        const isVertical = _menu.orientation === MENU_ORIENTATION.VERTICAL;
        const topLevelProps = Object.assign(Object.assign({}, checkMidclickProps), { onKeyUp: checkMidKeyPressProps.onKeyUp, onKeyDown,
            onFocus,
            onBlur });
        return Object.assign({ role: 'menu', 'aria-orientation': isVertical ? 'vertical' : 'horizontal', tabIndex: isTopLevel ? 0 : -1 }, (isTopLevel ? topLevelProps : {}));
    }, [
        menu,
        onFocus,
        onBlur,
        checkMidclickProps,
        checkMidKeyPressProps,
        getMenuItem,
        onKeyDown,
    ]);
    const getMenuItemProps = React.useCallback((path) => {
        const isTopLevel = path.length === 1;
        const isActivePath = equals(path, activePath);
        const useActiveStyles = isOpen && equals(path, activePath);
        const styles = useActiveStyles ? activeStyles : inactiveStyles;
        return Object.assign(Object.assign({}, checkMidclickProps), { role: 'menuitem', 'aria-roledescription': getAriaRoleDescription(activeParentMenu.orientation), id: getItemId(path), onKeyDown: equals(path, activePath) ? onKeyDown : undefined, onKeyUp: checkMidKeyPressProps.onKeyUp, tabIndex: -1, onFocus: () => {
                if (!isMidClick) {
                    setActivePath(path);
                }
            }, onMouseDown: () => {
                checkMidclickProps.onMouseDown();
                const menuItem = getMenuItem(path);
                if ((menuItem === null || menuItem === void 0 ? void 0 : menuItem.action.type) === MENU_ACTION_TYPE.LINK) {
                    window.location.href = menuItem.action.to;
                }
            }, onClick: (e) => {
                const menuItem = getMenuItem(path);
                const isSubmenu = (menuItem === null || menuItem === void 0 ? void 0 : menuItem.action.type) === MENU_ACTION_TYPE.SUBMENU;
                if (!isSubmenu) {
                    e.preventDefault();
                }
                if (isSubmenu &&
                    isActivePath &&
                    isTopLevel &&
                    !isMidKeyPress.current &&
                    isOpen) {
                    close();
                }
                else if (isSubmenu &&
                    isTopLevel &&
                    !isMidKeyPress.current &&
                    (!isOpen || !isActivePath)) {
                    setActivePath(path);
                    open();
                }
                else {
                    onKeyDown({
                        key: KEY_CODES.ENTER,
                    });
                }
            }, style: styles });
    }, [
        getItemId,
        getMenuItem,
        activePath,
        checkMidclickProps,
        isMidKeyPress,
        checkMidKeyPressProps,
        isOpen,
        close,
        open,
        onKeyDown,
        activeParentMenu,
        isMidClick,
    ]);
    const contextValue = {
        menu,
        isOpen,
        open,
        close,
        activePath,
        getMenuItem,
        getMenuProps,
        getMenuItemProps,
    };
    return (_jsx(MenuContext.Provider, Object.assign({ value: contextValue }, { children: typeof children === 'function' ? children(contextValue) : children })));
};
